import React, { useCallback, useEffect, useRef, useState } from "react";
import useApiError from "@hooks/useApiError";
import useApiHook from "@hooks/useApiHook";
import FatsuNewsElement from "@assets/images/FatsuNewsElement.webp";
import LatestNewsBadge from "@assets/images/LatestNewsBadge.webp";
import DOMPurify from "dompurify";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import ProductVideo from "../Products/ProductVideo";
import { Helmet } from "react-helmet";
import NewsCategory from "@src/components/News/NewsCategory";
import InLightNewsSkeleton from "@src/components/Skeleton/News/InLightNewsSkeleton";
import { getImageUrlWithExpiry } from "@src/utils/ImageExpirtyHelper";
import useReactQuery from "@src/hooks/useReactQuery";
import useScrollRestoration from "@src/hooks/useScrollRestoration";
import "@src/styles/NewsStyle.scss";
import type { INewsItem } from "@src/types/AppTypes";

const News: React.FC = () => {
  const newsApiEndpoint = useApiHook({ method: "get", url: "/news" });
  const ref = useRef<HTMLDivElement | null>(null);
  const [newsList, setNewsList] = useState<INewsItem[]>([]);
  const [inLightNews, setInLightNews] = useState<INewsItem[]>([]);
  const [isNewsVisible, setIsNewsVisible] = useState(false);
  const [inHouseList, setInHouseList] = useState<INewsItem[]>([]);
  const [latestNews, setLatestNews] = useState<INewsItem>();
  const setApiError = useApiError();

  const returnBgImages = useCallback((index: number) => {
    const remainder = index % 5;
    switch (remainder) {
      case 0:
        return "mb-20 max-md:mb-5 bg-radial-gradient p-6 border-2 cursor-pointer duration-300 border-black hover:opacity-80 max-sm:rotate-0";
      case 1:
        return "mb-20 bg-[url('@assets/images/news/pink-dot-bg.webp')] border-black bg-no-repeat bg-cover bg-center p-6 border-2 cursor-pointer duration-300 hover:opacity-80 max-sm:rotate-0";
      case 2:
        return "mb-20 bg-[url('@assets/images/news/blue-dot-bg.webp')] bg-no-repeat bg-cover bg-center p-6 border-2 border-black duration-300 hover:opacity-80 cursor-pointer max-sm:rotate-0";
      case 3:
        return "mb-20 bg-[url('@assets/images/news/pink-dot-bg.webp')] bg-no-repeat bg-cover cursor-pointer bg-center p-6 border-2 border-black duration-300 hover:opacity-80 max-sm:rotate-0";
      case 4:
        return "mb-20 bg-radial-gradient p-6 border-2 border-black duration-300 hover:opacity-80 cursor-pointer max-sm:rotate-0";
      default:
        return "my-10 bg-radial-gradient p-6 border-2 border-black duration-300 hover:opacity-80 max-sm:rotate-0 cursor-pointer";
    }
  }, []);

  const {
    data: newsData,
    isSuccess: newsIsSuccess,
    error: newsError,
    isLoading: newsIsLoading,
  } = useReactQuery<{
    status: boolean;
    message: string;
    news: INewsItem[];
    latest_news?: INewsItem;
  }>({
    args: { queryKey: ["get-news"] },
    queryFn: () => newsApiEndpoint(null, "", null),
  });

  useEffect(() => {
    if (newsIsSuccess && newsData) {
      setInLightNews(
        newsData.data.news.filter((v) => v.news_type === "fatSu in Lights")
      );
      setNewsList(
        newsData.data.news.filter((v) => v.news_type === "In the News")
      );
      setInHouseList(
        newsData.data.news.filter((v) => v.news_type === "In-House News")
      );
      setLatestNews(newsData.data.latest_news);
    } else if (newsError) {
      setApiError(newsError);
    }
  }, [newsIsSuccess, newsData, newsError, setApiError]);

  useEffect(() => {
    const newsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNewsVisible(true); // Component is visible, load it
          newsObserver.disconnect(); // disconnect observer once component load
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.2,
      }
    );

    if (ref.current) {
      newsObserver.observe(ref.current);
    }

    const oldNewsRef = ref;

    return () => {
      if (oldNewsRef.current) {
        newsObserver.unobserve(oldNewsRef.current);
      }
    };
  }, []);

  useScrollRestoration();

  return (
    <main className="min-h-[inherit]">
      <Helmet>
        <title>fatSu | News</title>
        <meta
          name="keywords"
          content="fatsu, fatsu news, fatsu in lights, fatsu in the news, Latest news, Latest fatsu news, Trending articles, News updates, Information source"
        />
        <meta
          name="description"
          content="Stay updated with the latest news and developments of fatSu. Browse through articles, and updates on trending topics."
        />
      </Helmet>
      <section className="section relative text-white py-10 max-sm:py-6 max-md:px-4 bg-[url('@assets/images/NewsBackBG.webp')] bg-no-repeat bg-cover min-h-[inherit]">
        <div className="container mx-auto max-md:px-0">
          {newsIsLoading ? (
            <div className="text-white min-h-[900px] relative">
              <InLightNewsSkeleton />
            </div>
          ) : latestNews ? (
            <div className="text-white relative">
              <div>
                <h2 className="text-[#91DBE7] text-6xl max-md:text-4xl max-sm:text-3xl text-start mb-5 break-all">
                  Fatsu <span className="text-primary">In Lights</span>
                </h2>
                <div className="relative z-[2] flex items-end justify-end pr-20 max-lg:flex-col-reverse max-md:pr-0">
                  <div className="lg:-mr-6 bg-[url(@assets/images/NewsDescription.webp)] bg-center bg-no-repeat bg-[length:100%_100%] max-w-[500px] max-sm:max-w-full min-w-[300px] mb-24 max-lg:mb-0">
                    <div className="-rotate-6 text-center py-32 px-12 w-full">
                      <h3 className="text-[#91DBE7] text-3xl mb-3 truncate">
                        {latestNews?.title}
                      </h3>
                      <div
                        className="text-primary text-center text-2xl line-clamp-5 max-md:line-clamp-3 font-sans-normal break-words text-wrap"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            latestNews?.description || ""
                          ),
                        }}
                      ></div>
                      <button
                        onClick={() => {
                          const url = `/${latestNews?.news_type
                            .toLowerCase()
                            .replace(/ /g, "-")}/${latestNews?.slug}`;
                          window.open(url, "_blank", "noopener");
                        }}
                        className="underline uppercase"
                        aria-label="Read more"
                      >
                        Read More
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <img
                      src={FatsuNewsElement}
                      className="absolute -right-20 bottom-14 -z-[1] bg-contain w-11/12 max-md:right-0"
                      alt=""
                    />
                    <div>
                      <img
                        src={LatestNewsBadge}
                        className="max-w-72 h-44 object-contain mx-10 -mb-4 max-sm:mx-0"
                        alt=""
                      />
                      <div className="bg-radial-gradient-light h-[560px] p-4 rounded-[20%] max-sm:rounded-xl w-[400px] max-[430px]:w-[300px] first-news-slide news-main-page">
                        <Carousel
                          showStatus={false}
                          showThumbs={false}
                          infiniteLoop={true}
                        >
                          {latestNews?.thumbnail && (
                            <div className="relative h-[525px] rounded-[10%] my-auto w-full overflow-hidden">
                              <img
                                className="h-full w-full object-contain relative z-10"
                                src={getImageUrlWithExpiry(
                                  latestNews.thumbnail
                                )}
                                alt="product img"
                              />
                              <img
                                className="h-full w-full object-cover absolute top-0 left-0 blur-sm"
                                src={getImageUrlWithExpiry(
                                  latestNews.thumbnail
                                )}
                                alt="product img"
                              />
                            </div>
                          )}
                          {latestNews?.images &&
                            latestNews.images.map((item) => {
                              return (
                                <div
                                  className="relative h-[525px] rounded-[10%] my-auto w-full overflow-hidden"
                                  key={item}
                                >
                                  <img
                                    src={getImageUrlWithExpiry(item)}
                                    className="h-full w-full object-contain relative z-10"
                                    alt="news-cover-image"
                                  />
                                  <img
                                    className="h-full w-full object-cover absolute top-0 left-0 blur-sm"
                                    src={getImageUrlWithExpiry(item)}
                                    alt="news-cover-image"
                                  />
                                </div>
                              );
                            })}
                          {[latestNews?.video]
                            .filter((i) => !!i)
                            .map((item) => {
                              return (
                                <ProductVideo
                                  vidUrl={item}
                                  isFromNewsPage={true} // this is for style
                                />
                              );
                            })}
                        </Carousel>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <h1
              className={`text-primary text-3xl max-sm:text-2xl ${
                latestNews
                  ? "min-h-[900px]"
                  : "min-h-[600px] max-md:min-h-[400px]"
              } flex justify-center items-center`}
            >
              Fatsu news coming soon!
            </h1>
          )}
        </div>
      </section>
      <div ref={ref}>
        {isNewsVisible ? (
          <NewsCategory
            newsList={newsList}
            inHouseList={inHouseList}
            inLightNews={inLightNews}
            returnBgImages={(index: number) => returnBgImages(index)}
          />
        ) : (
          <div className="flex justify-center items-center">
            <i className="ri-loop-right-line btn-loader"></i>
          </div>
        )}
      </div>
    </main>
  );
};

export default News;
